import { motion } from "framer-motion";
import { HOME } from "@/constants/testIds";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  return (
    <section className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-white overflow-hidden">
      <div className="px-5 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 mb-6 md:mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-brand-red" />
            <span className="text-xs md:text-sm font-display font-semibold tracking-[0.18em] uppercase text-brand-slate">
              15+ Years · Global Talent Partner
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
            className="font-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] text-brand-ink"
          >
            Your Trusted
            <br />
            Recruitment Partner
            <br />
            Across{" "}
            <span className="text-brand-blue">US, Europe</span>{" "}
            <span className="text-brand-ink">&</span>{" "}
            <span className="text-brand-red">Canada</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7, ease: EASE }}
            className="mt-6 md:mt-8 max-w-2xl text-base md:text-[17px] text-brand-slate leading-relaxed"
          >
            We deliver AI-powered recruitment, staffing, executive search, and
            Recruitment Process Outsourcing (RPO) solutions that help businesses
            build high-performing teams across the United States, Europe, and
            Canada. Backed by more than 15 years of recruitment excellence, we
            combine advanced technology, market intelligence, and human
            expertise to identify, engage, and secure top talent faster and
            more efficiently. Our specialized recruitment services support
            organizations across Construction, Healthcare, Engineering,
            Manufacturing, Information Technology, Finance, Accounting, Supply
            Chain, Logistics, Executive Leadership, and other high-demand
            industries—providing scalable hiring solutions that reduce
            time-to-hire, improve candidate quality, and fuel long-term
            business success.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7, ease: EASE }}
            className="mt-8 md:mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#post-job"
              data-testid={HOME.heroPrimaryCta}
              className="font-display font-semibold text-base px-7 py-4 bg-brand-blue text-white rounded-md hover:bg-brand-blue-dark transition-all hover:-translate-y-0.5 shadow-[0_6px_16px_rgba(0,75,135,0.25)]"
            >
              Post a Job
            </a>
            <a
              href="#submit-resume"
              data-testid={HOME.heroSecondaryCta}
              className="font-display font-semibold text-base px-7 py-4 bg-brand-red text-white rounded-md hover:bg-brand-red-dark transition-all hover:-translate-y-0.5 shadow-[0_6px_16px_rgba(255,59,59,0.25)]"
            >
              Submit Resume
            </a>
          </motion.div>
        </motion.div>

        {/* Right: image with decorative frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.9, ease: EASE }}
          className="lg:col-span-6 relative"
        >
          <div className="relative">
            {/* Decorative offset frame */}
            <div className="absolute -top-4 -right-4 w-full h-full rounded-2xl border-2 border-brand-blue hidden md:block" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-2xl bg-brand-red hidden md:block" />
            <div className="relative overflow-hidden rounded-2xl bg-brand-surface aspect-[4/3]">
              <img
                src="https://images.pexels.com/photos/3184639/pexels-photo-3184639.jpeg?auto=compress&cs=tinysrgb&w=1600"
                alt="Diverse leadership team in a modern glass boardroom"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
            </div>

            {/* Stat badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6, ease: EASE }}
              className="absolute -bottom-6 md:-bottom-8 right-4 md:right-10 bg-white card-soft rounded-xl px-5 md:px-6 py-4 md:py-5 flex items-center gap-4"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-brand-blue-light flex items-center justify-center">
                <span className="font-display font-bold text-brand-blue text-lg md:text-xl">15+</span>
              </div>
              <div>
                <div className="font-display font-bold text-brand-ink leading-tight">Years placing</div>
                <div className="text-sm text-brand-muted leading-tight">exceptional talent</div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
